import React, { useState, useMemo, useEffect } from 'react';
import { Card, Button, Form, Table, Input, Select, Row, Col, Typography, App } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { purchaseReturnService, type ReturnableSourceLine } from '@/services/purchaseReturn.service';
import { goodsReceiptNoteService } from '@/services/goodsReceiptNote.service';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';
import { headerProjectLabel } from '@/utils/projectDisplay';
import {
  getCriticalItemRowClassName,
  ItemCodeWithCritical,
} from '@/components/inventory/criticalItemUi';
import { useCriticalItemLookup } from '@/hooks/useCriticalItemLookup';
import { LineSpecificationsText } from '@/components/procurement/LineSpecifications';

const { Title } = Typography;

export default function CreatePurchaseReturnPage() {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [selectedGrnId, setSelectedGrnId] = useState<string | null>(null);
  const [lineQuantities, setLineQuantities] = useState<Record<string, number>>({});
  const [returnReason, setReturnReason] = useState<string>('');
  const [remarks, setRemarks] = useState<string>('');
  const { isCriticalCode } = useCriticalItemLookup();

  const { data: sources = [], isLoading: loadingSources } = useQuery({
    queryKey: ['purchase-return-returnable-sources'],
    queryFn: () => purchaseReturnService.getReturnableSources(),
  });

  const grnOptions = useMemo(() => {
    const seen = new Set<string>();
    const out: { value: string; label: string }[] = [];
    for (const s of sources) {
      if (!seen.has(s.grn_id)) {
        seen.add(s.grn_id);
        out.push({ value: s.grn_id, label: `${s.grn_number} (${s.grn_id})` });
      }
    }
    return out;
  }, [sources]);

  const { data: grn, isLoading: loadingGrn } = useQuery({
    queryKey: ['grn', selectedGrnId],
    queryFn: () => goodsReceiptNoteService.getById(selectedGrnId!),
    enabled: !!selectedGrnId,
  });

  const linesForGrn = useMemo(() => {
    if (!selectedGrnId) return [];
    return sources.filter((s) => s.grn_id === selectedGrnId);
  }, [sources, selectedGrnId]);

  const createMutation = useMutation({
    mutationFn: (payload: {
      return_date: string;
      supplier_id: string;
      grn_id: string;
      department_id?: string;
      return_reason?: string;
      remarks?: string;
      lines: Array<{ grn_item_id: string; return_quantity: number; return_reason?: string }>;
    }) => purchaseReturnService.create(payload),
    onSuccess: (data) => {
      message.success('Purchase return created');
      queryClient.invalidateQueries({ queryKey: ['purchase-returns'] });
      queryClient.invalidateQueries({ queryKey: ['purchase-return-returnable-sources'] });
      navigate(`/scm/procurement/purchase-returns/${data.id}`);
    },
    onError: (e: any) => {
      message.error(e?.response?.data?.message || 'Failed to create purchase return');
    },
  });

  const handleGrnChange = (grnId: string) => {
    setSelectedGrnId(grnId || null);
    setLineQuantities({});
  };

  useEffect(() => {
    if (grn?.department_id) {
      form.setFieldValue('department_id', grn.department_id);
    }
  }, [grn?.department_id, form]);

  const handleQuantityChange = (grnItemId: string, value: number) => {
    setLineQuantities((prev) => ({ ...prev, [grnItemId]: value }));
  };

  const onFinish = async () => {
    if (!selectedGrnId || !grn?.supplier_id) {
      message.error('Select a GRN first');
      return;
    }
    try {
      await form.validateFields(['return_date', 'department_id']);
    } catch {
      return;
    }
    const lines = linesForGrn
      .map((line) => ({
        grn_item_id: line.grn_item_id,
        return_quantity: lineQuantities[line.grn_item_id] ?? 0,
        return_reason: returnReason || undefined,
      }))
      .filter((l) => l.return_quantity > 0);
    if (lines.length === 0) {
      message.error('Enter at least one return quantity');
      return;
    }
    const returnDate = form.getFieldValue('return_date') || dayjs().format('YYYY-MM-DD');
    createMutation.mutate({
      return_date: returnDate,
      supplier_id: grn.supplier_id,
      grn_id: selectedGrnId,
      department_id: form.getFieldValue('department_id'),
      return_reason: returnReason || undefined,
      remarks: remarks || undefined,
      lines,
    });
  };

  const columns: ColumnsType<ReturnableSourceLine> = [
    {
      title: 'Item',
      dataIndex: 'item_code',
      key: 'item_code',
      width: 140,
      render: (code: string, r) => (
        <ItemCodeWithCritical code={code} critical={isCriticalCode(r.item_code)} />
      ),
    },
    {
      title: 'Description',
      dataIndex: 'item_description',
      key: 'item_description',
      render: (desc: string, r) => (
        <>
          <div>{desc}</div>
          <LineSpecificationsText value={r.specifications} />
        </>
      ),
    },
    {
      title: 'Supplier Description',
      dataIndex: 'supplier_description',
      key: 'supplier_description',
      render: (v: string, r: ReturnableSourceLine) => v || r.item_description || '—',
    },
    { title: 'Returnable', dataIndex: 'returnable_quantity', key: 'returnable_quantity', width: 100 },
    { title: 'UOM', dataIndex: 'uom', key: 'uom', width: 60 },
    {
      title: 'Return Qty',
      key: 'return_quantity',
      width: 120,
      render: (_, record) => (
        <Input
          type="number"
          min={0}
          max={record.returnable_quantity}
          value={lineQuantities[record.grn_item_id] ?? ''}
          onChange={(e) => handleQuantityChange(record.grn_item_id, Number(e.target.value) || 0)}
          placeholder="0"
        />
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/scm/procurement/purchase-returns')} style={{ marginBottom: 16 }}>
        Back to Purchase Returns
      </Button>

      <Title level={4} style={{ marginBottom: 24 }}>
        Create Purchase Return
      </Title>

      <Form form={form} layout="vertical" initialValues={{ return_date: dayjs().format('YYYY-MM-DD') }}>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item name="return_date" label="Return Date" rules={[{ required: true }]}>
              <Input type="date" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="GRN (source)">
              <Select
                placeholder="Select GRN"
                allowClear
                style={{ width: '100%' }}
                options={grnOptions}
                value={selectedGrnId ?? undefined}
                onChange={handleGrnChange}
                loading={loadingSources}
              />
            </Form.Item>
          </Col>
        </Row>
        {selectedGrnId && grn && (
          <>
            <p style={{ marginBottom: 8 }}>
              Supplier: <strong>{grn.supplier?.supplier_name ?? grn.supplier_id}</strong>
            </p>
            <Form.Item label="Project">
              <Input readOnly value={headerProjectLabel(grn, grn.items)} />
            </Form.Item>
            <Form.Item
              name="department_id"
              label="Department"
              rules={[{ required: true, message: 'Department is required' }]}
            >
              <DepartmentSelect />
            </Form.Item>
            <Form.Item label="Return reason (optional)">
              <Input value={returnReason} onChange={(e) => setReturnReason(e.target.value)} placeholder="Reason for return" />
            </Form.Item>
            <Form.Item label="Remarks (optional)">
              <Input.TextArea value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Remarks" rows={2} />
            </Form.Item>
            <Table
              size="small"
              loading={loadingGrn}
              columns={columns}
              dataSource={linesForGrn.map((l) => ({ ...l, key: l.grn_item_id }))}
              rowClassName={(r) =>
                getCriticalItemRowClassName({ is_critical: isCriticalCode(r.item_code) })
              }
              pagination={false}
              style={{ marginBottom: 16 }}
            />
            <Button type="primary" onClick={onFinish} loading={createMutation.isPending}>
              Create Purchase Return
            </Button>
          </>
        )}
      </Form>
    </div>
  );
}
